import { assertNever } from '../assert-never';
import type { DimensionText } from '../dimensions';
import { normalizeDegrees, type Shape, type Vec2 } from '../geom';
import { DEFAULT_LAYER, LINE_TYPES, type Layer, type Style, type Unit } from '../model';
import { layoutsOf, type Drawn } from '../scene';

const WHITE = 7;
const LOCKED = 4;

/** DXF's `$INSUNITS` code for each unit. */
const INSUNITS: Record<Unit, number> = { in: 1, ft: 2, mm: 4, cm: 5, m: 6 };

/**
 * Minimal AutoCAD R12 DXF: enough for lines, circles and arcs to open in any CAD tool, each
 * on its own layer. Every layer is in the table, a hidden one switched off; hidden entities
 * are left out. Colors become the nearest AutoCAD color index, and R12 has no line weights,
 * so a style's thickness is not written. Given the unit, a header names it as `$INSUNITS`, so
 * CAD tools open the drawing at its physical size.
 *
 * Dimensions are written as the lines, arcs, `SOLID` arrowheads and `TEXT` they draw, on
 * their layer, so every reader shows them alike; CAD tools see plain geometry, not editable
 * dimensions.
 */
export function toDxf(
  drawn: Drawn,
  layers: readonly Layer[] = [DEFAULT_LAYER],
  unit?: Unit,
): string {
  const out: string[] = [];
  const pair = (code: number, value: string | number) => out.push(String(code), String(value));
  const names = new Map(layers.map((layer) => [layer.id, dxfName(layer.name)]));
  const layerStyles = new Map(layers.map((layer) => [layer.id, layer.style]));

  if (unit) {
    pair(0, 'SECTION');
    pair(2, 'HEADER');
    pair(9, '$INSUNITS');
    pair(70, INSUNITS[unit]);
    pair(9, '$MEASUREMENT');
    pair(70, unit === 'in' || unit === 'ft' ? 0 : 1);
    pair(0, 'ENDSEC');
  }
  pair(0, 'SECTION');
  pair(2, 'TABLES');
  pair(0, 'TABLE');
  pair(2, 'LTYPE');
  pair(70, Object.keys(LINE_TYPES).length);
  for (const { dxf, pattern } of Object.values(LINE_TYPES)) {
    pair(0, 'LTYPE');
    pair(2, dxf);
    pair(70, 0);
    pair(3, '');
    pair(72, 65);
    pair(73, pattern.length);
    pair(
      40,
      pattern.reduce((sum: number, length) => sum + length, 0),
    );
    // DXF writes a gap as a negative length.
    pattern.forEach((length, index) => pair(49, index % 2 === 0 ? length : -length));
  }
  pair(0, 'ENDTAB');
  pair(0, 'TABLE');
  pair(2, 'LAYER');
  pair(70, layers.length);
  for (const layer of layers) {
    pair(0, 'LAYER');
    pair(2, dxfName(layer.name));
    pair(70, layer.locked ? LOCKED : 0);
    const color = layer.style?.color ? nearestColor(layer.style.color) : WHITE;
    // A negative colour is how DXF says a layer is off.
    pair(62, layer.visible ? color : -color);
    pair(6, LINE_TYPES[layer.style?.line ?? 'solid'].dxf);
  }
  pair(0, 'ENDTAB');
  pair(0, 'ENDSEC');

  pair(0, 'SECTION');
  pair(2, 'ENTITIES');

  for (const entity of drawn.entities) {
    if (!entity.visible) continue;
    writeShape(pair, entity.shape, names.get(entity.layerId) ?? DEFAULT_LAYER.name, () =>
      writeOverrides(pair, entity.style, layerStyles.get(entity.layerId)),
    );
  }

  for (const annotation of layoutsOf(drawn)) {
    if (!annotation.visible) continue;
    const layer = names.get(annotation.layerId) ?? DEFAULT_LAYER.name;
    const overrides = () =>
      writeOverrides(pair, annotation.style, layerStyles.get(annotation.layerId));
    for (const shape of annotation.layout.strokes) writeShape(pair, shape, layer, overrides);
    for (const polygon of annotation.layout.fills) writeSolid(pair, polygon, layer, overrides);
    writeText(pair, annotation.layout.text, layer, overrides);
  }

  pair(0, 'ENDSEC');
  pair(0, 'EOF');
  return out.join('\n') + '\n';
}

type Pair = (code: number, value: string | number) => void;

/** An entity's color and line type, where they differ from its layer's. */
function writeOverrides(pair: Pair, style: Style | undefined, layer: Style | undefined): void {
  if (style?.line && style.line !== (layer?.line ?? 'solid')) pair(6, LINE_TYPES[style.line].dxf);
  if (style?.color && style.color !== layer?.color) pair(62, nearestColor(style.color));
}

function writeShape(pair: Pair, shape: Shape, layer: string, overrides: () => void): void {
  switch (shape.type) {
    case 'line':
      pair(0, 'LINE');
      pair(8, layer);
      overrides();
      pair(10, n(shape.a.x));
      pair(20, n(shape.a.y));
      pair(11, n(shape.b.x));
      pair(21, n(shape.b.y));
      return;
    case 'circle':
      pair(0, 'CIRCLE');
      pair(8, layer);
      overrides();
      pair(10, n(shape.center.x));
      pair(20, n(shape.center.y));
      pair(40, n(shape.radius));
      return;
    case 'arc':
      pair(0, 'ARC');
      pair(8, layer);
      overrides();
      pair(10, n(shape.center.x));
      pair(20, n(shape.center.y));
      pair(40, n(shape.radius));
      pair(50, n(normalizeDegrees(shape.start)));
      pair(51, n(normalizeDegrees(shape.end)));
      return;
    default:
      assertNever(shape);
  }
}

/** A filled triangle or quadrilateral; DXF orders a SOLID's corners in a zigzag. */
function writeSolid(pair: Pair, polygon: readonly Vec2[], layer: string, overrides: () => void) {
  const [a, b, c, d] = polygon;
  if (!a || !b || !c) return;
  pair(0, 'SOLID');
  pair(8, layer);
  overrides();
  [a, b, d ?? c, c].forEach((p, index) => {
    pair(10 + index, n(p.x));
    pair(20 + index, n(p.y));
  });
}

/** Text centered on its point, both ways. */
function writeText(pair: Pair, text: DimensionText, layer: string, overrides: () => void) {
  pair(0, 'TEXT');
  pair(8, layer);
  overrides();
  pair(10, n(text.at.x));
  pair(20, n(text.at.y));
  pair(40, n(text.height));
  pair(1, dxfText(text.content));
  pair(50, n(normalizeDegrees(text.angle)));
  pair(72, 1);
  pair(11, n(text.at.x));
  pair(21, n(text.at.y));
  pair(73, 2);
}

/** R12 text is plain ASCII, with control codes for the symbols dimensions use. */
const dxfText = (content: string): string =>
  content.replace(/Ø/g, '%%c').replace(/°/g, '%%d').replace(/±/g, '%%p');

const n = (value: number): number => Number(value.toFixed(6));

/** DXF forbids these characters in a layer name. */
const dxfName = (name: string): string => name.replace(/[<>/\\":;?*|=,`]/g, '_');

/**
 * The AutoCAD color index nearest `#rrggbb`. Index 7 prints black on paper and shows white
 * on a dark screen, so it stands for black here.
 */
export function nearestColor(hex: string): number {
  const rgb = [1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16));
  let best = WHITE;
  let bestDistance = Infinity;
  ACI.forEach((candidate, index) => {
    const distance = candidate.reduce((sum, value, at) => sum + (value - rgb[at]!) ** 2, 0);
    if (distance < bestDistance) [best, bestDistance] = [index, distance];
  });
  return best;
}

/** The AutoCAD color index palette, by index; 0 is not a color, so it can never match. */
const ACI: readonly (readonly number[])[] = (() => {
  const table: number[][] = [[Infinity, Infinity, Infinity]];
  table.push([255, 0, 0], [255, 255, 0], [0, 255, 0], [0, 255, 255], [0, 0, 255], [255, 0, 255]);
  table.push([0, 0, 0], [128, 128, 128], [192, 192, 192]);
  // 10 to 249: 24 hues 15 degrees apart, each in five shades, full and half saturation.
  const shades = [1, 0.65, 0.5, 0.3, 0.15];
  for (let index = 10; index < 250; index += 1) {
    const hue = (Math.floor(index / 10) - 1) * 15;
    const shade = shades[Math.floor((index % 10) / 2)]!;
    table.push(hsv(hue, index % 2 === 0 ? 1 : 0.5, shade));
  }
  for (const gray of [51, 91, 132, 173, 214, 255]) table.push([gray, gray, gray]);
  return table;
})();

function hsv(hue: number, saturation: number, value: number): number[] {
  const channel = (offset: number) => {
    const k = (offset + hue / 60) % 6;
    return Math.round(255 * value * (1 - saturation * Math.max(0, Math.min(k, 4 - k, 1))));
  };
  return [channel(5), channel(3), channel(1)];
}
